import type { FeatureCollection } from 'geojson';
import type Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import type { Geometry } from 'ol/geom';
import type BaseLayer from 'ol/layer/Base';
import Group from 'ol/layer/Group';
import Layer from 'ol/layer/Layer';
import type { Source } from 'ol/source';
import type VectorSource from 'ol/source/Vector';

import { MapSetup } from '#nora-ui/map/map/MapSetup';

/**
 * Utility class for map-related operations.
 */
export class MapUtils {
  /**
   * Converts an angle in degrees to radians.
   * @param angleDegrees Angle in degrees.
   * @returns Angle in radians.
   */
  static degreesToRadians(angleDegrees: number): number {
    return angleDegrees * (Math.PI / 180);
  }

  /**
   * Converts a user heading in degrees to map rotation in radians for heading-up mode.
   * @param headingDegrees Heading angle in degrees.
   * @returns Map rotation angle in radians.
   */
  static userHeadingDegreesToMapRotationRadians(headingDegrees: number): number {
    return MapUtils.degreesToRadians(headingDegrees) * -1;
  }

  /**
   * Recursively flattens layers from a Group or array of BaseLayers into a single array of Layers.
   * @param layers
   * @returns An array of flattened Layers.
   */
  static getLayersFlattened(
    layers: BaseLayer[] | Group,
  ): Layer<Source>[] {
    const out: Layer<Source>[] = [];
    let iterable: BaseLayer[];

    if (layers instanceof Group) {
      iterable = layers.getLayers().getArray();
    }
    else {
      iterable = layers;
    }

    iterable.forEach((layer) => {
      if (layer instanceof Layer) {
        out.push(layer as Layer<Source>);
      }
      else if (layer instanceof Group) {
        const childLayers = MapUtils.getLayersFlattened(layer);

        out.push(...childLayers);
      }
    });

    return out;
  }

  /**
   * Replaces all features in the given vector source with those from the provided GeoJSON FeatureCollection.
   * @param source The vector source to update.
   * @param featureCollection The GeoJSON FeatureCollection containing new features.
   */
  static replaceVectorSourceGeoJson(
    source: VectorSource,
    featureCollection: FeatureCollection,
  ): void {
    const features = featureCollection.features.map(feature => ({
      ...feature,
      properties: {
        ...feature.properties,
        id: feature.id,
      },
    }));

    source.clear();
    source.addFeatures(
      new GeoJSON().readFeatures(
        { ...featureCollection, features },
        {
          dataProjection: MapSetup.WGS84,
          featureProjection: MapSetup.WEB_MERCATOR,
        },
      ),
    );
  }

  /**
   * Removes a feature from the given vector source by feature instance or feature ID.
   * @param featureOrFeatureId The feature instance or feature ID to remove.
   * @param source The vector source from which to remove the feature.
   */
  static removeFeatureFromSource(
    featureOrFeatureId: Feature<Geometry> | string,
    source: VectorSource,
  ): void {
    if (typeof featureOrFeatureId === 'string') {
      const feature = source.getFeatureById(featureOrFeatureId);
      if (feature) {
        source.removeFeature(feature);
      }
    }
    else {
      source.removeFeature(featureOrFeatureId);
    }
  }
}
